import React from "react";
import dayjs from "dayjs";
import PrintOutHeader from "../../../../components/shared/PrintOutHeader";
import { useTimeSheetStore } from "../../../../config/stores";
import appConfig from "../../../../config/config.json";
import useTimeSheetGrid, {
  DAY_TYPE_STYLES,
  formatHours,
} from "../TimeSheetActivities/useTimeSheetGrid";

const printStyles = `
  .ts-print { font-family: Arial, Helvetica, sans-serif; font-size: 8.5pt; color: #000; }
  .ts-print table { width: 100%; border-collapse: collapse; }
  .ts-print th, .ts-print td {
    border: 1px solid #555;
    padding: 3px 4px;
    vertical-align: top;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
  .ts-print .ts-title { text-align: center; font-size: 12pt; font-weight: bold; margin: 6px 0; }
  .ts-print .ts-info td { font-size: 8.5pt; }
  .ts-print .ts-info .ts-label { font-weight: bold; background: #f1f3f5; width: 14%; }
  .ts-print .ts-grid { table-layout: fixed; margin-top: 6px; }
  .ts-print .ts-grid thead { display: table-header-group; }
  .ts-print .ts-grid tfoot { display: table-row-group; }
  .ts-print .ts-grid th { background: #dee2e6; font-size: 7pt; text-align: center; vertical-align: middle; }
  .ts-print .ts-grid tr { page-break-inside: avoid; break-inside: avoid; }
  .ts-print .ts-center { text-align: center; }
  .ts-print .ts-bold { font-weight: bold; }
  .ts-print .ts-muted { color: #444; font-size: 7.5pt; }
  .ts-print .ts-activity { margin-bottom: 2px; }
  .ts-print .ts-summary td { background: #f1f3f5; font-weight: bold; }
  .ts-print .ts-legend { display: flex; gap: 14px; margin-top: 6px; font-size: 8pt; }
  .ts-print .ts-swatch { display: inline-block; width: 12px; height: 10px; border: 1px solid #555; margin-right: 4px; vertical-align: middle; }
  .ts-print .ts-signatures { margin-top: 14px; page-break-inside: avoid; break-inside: avoid; }
  .ts-print .ts-signatures td { height: 48px; width: 25%; }
`;

const Signature = ({ path }) =>
  path ? (
    <img
      src={`${appConfig.server_url}${path}`}
      alt="signature"
      style={{ height: 34, maxWidth: 140, objectFit: "contain" }}
    />
  ) : null;

const fullName = (person) =>
  `${person?.first_name || ""} ${person?.other_names || ""}`.trim();

const PrintTimeSheet = () => {
  const { timeSheetDetails } = useTimeSheetStore();
  const grid = useTimeSheetGrid(timeSheetDetails);

  const approvedAt = dayjs(timeSheetDetails.approved_at);
  const submittedAt = dayjs(timeSheetDetails.submitted_at);
  const staffName = fullName(timeSheetDetails?.user);
  const approverName = fullName(timeSheetDetails?.approved_by);

  const DATE_WIDTH = 6.5;
  const DAY_WIDTH = 6;
  const TOTAL_WIDTH = 4.5;
  const ACTIVITIES_WIDTH = grid.columns.length > 8 ? 30 : 36;
  const projectWidth =
    (100 - DATE_WIDTH - DAY_WIDTH - TOTAL_WIDTH - ACTIVITIES_WIDTH) /
    Math.max(grid.columns.length, 1);

  return (
    <div className="ts-print">
      <style>{printStyles}</style>
      <PrintOutHeader />
      <div className="ts-title">
        {`${grid.monthName} ${grid.year} TIME SHEET`.toUpperCase()}
      </div>

      <table className="ts-info">
        <tbody>
          <tr>
            <td className="ts-label">NAME OF STAFF</td>
            <td>{staffName}</td>
            <td className="ts-label">PERIOD</td>
            <td>{grid.period}</td>
          </tr>
          <tr>
            <td className="ts-label">POSITION</td>
            <td>{timeSheetDetails?.role?.role || ""}</td>
            <td className="ts-label">STATUS</td>
            <td style={{ textTransform: "uppercase" }}>
              {timeSheetDetails?.status || ""}
            </td>
          </tr>
          <tr>
            <td className="ts-label">APPROVED BY</td>
            <td>{approverName || "NOT APPROVED"}</td>
            <td className="ts-label">APPROVED AT</td>
            <td>
              {approvedAt.isValid()
                ? approvedAt.format("DD/MM/YYYY HH:mm")
                : "NOT APPROVED"}
            </td>
          </tr>
          <tr>
            <td className="ts-label">DAYS WORKED</td>
            <td>{grid.workedDays}</td>
            <td className="ts-label">TOTAL HOURS</td>
            <td>{formatHours(grid.totalHours) || 0}</td>
          </tr>
        </tbody>
      </table>

      <div className="ts-legend">
        {["weekend", "holiday", "leave"].map((type) => (
          <span key={type}>
            <span
              className="ts-swatch"
              style={{ backgroundColor: DAY_TYPE_STYLES[type].backgroundColor }}
            />
            {DAY_TYPE_STYLES[type].label} ({grid.counts[type]})
          </span>
        ))}
      </div>

      <table className="ts-grid">
        <colgroup>
          <col style={{ width: `${DATE_WIDTH}%` }} />
          <col style={{ width: `${DAY_WIDTH}%` }} />
          <col style={{ width: `${ACTIVITIES_WIDTH}%` }} />
          {grid.columns.map((column) => (
            <col key={column.key} style={{ width: `${projectWidth}%` }} />
          ))}
          <col style={{ width: `${TOTAL_WIDTH}%` }} />
        </colgroup>
        <thead>
          <tr>
            <th>DATE</th>
            <th>DAY</th>
            <th>ACTIVITIES</th>
            {grid.columns.map((column) => (
              <th key={column.key}>{column.title}</th>
            ))}
            <th>TOTAL</th>
          </tr>
        </thead>
        <tbody>
          {grid.rows.map((row) => {
            const cellStyle = {
              backgroundColor: DAY_TYPE_STYLES[row.type].backgroundColor,
            };
            return (
              <tr key={row.key}>
                <td style={cellStyle}>{row.date}</td>
                <td style={cellStyle}>
                  {row.dayName}
                  {row.holidayName && (
                    <div className="ts-bold">{row.holidayName}</div>
                  )}
                  {row.type === "leave" && <div className="ts-bold">{row.leaveName}</div>}
                </td>
                <td style={cellStyle}>
                  {row.activities.map((activity) => (
                    <div key={activity.id} className="ts-activity">
                      {activity.text}{" "}
                      <span className="ts-muted">
                        ({activity.columnTitle} · {formatHours(activity.hours)}
                        h)
                      </span>
                    </div>
                  ))}
                </td>
                {grid.columns.map((column) => (
                  <td key={column.key} className="ts-center" style={cellStyle}>
                    {formatHours(row.hoursByColumn[column.key])}
                  </td>
                ))}
                <td className="ts-center ts-bold" style={cellStyle}>
                  {formatHours(row.total)}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="ts-summary">
            <td colSpan={3}>TOTAL HOURS</td>
            {grid.columns.map((column) => (
              <td key={column.key} className="ts-center">
                {formatHours(grid.totalsByColumn[column.key])}
              </td>
            ))}
            <td className="ts-center">{formatHours(grid.totalHours) || 0}</td>
          </tr>
          <tr className="ts-summary">
            <td colSpan={3}>PERCENTAGE</td>
            {grid.columns.map((column) => (
              <td key={column.key} className="ts-center">
                {grid.percentagesByColumn[column.key]}
              </td>
            ))}
            <td className="ts-center">{grid.totalHours ? "100%" : ""}</td>
          </tr>
        </tfoot>
      </table>

      <table className="ts-signatures">
        <tbody>
          <tr>
            <td>
              <div className="ts-bold">PREPARED BY (STAFF)</div>
              <div>{staffName}</div>
              <Signature
                path={
                  timeSheetDetails?.user?.signature ||
                  timeSheetDetails?.created_by?.signature
                }
              />
            </td>
            <td>
              <div className="ts-bold">DATE SUBMITTED</div>
              {submittedAt.isValid() ? submittedAt.format("DD/MM/YYYY") : ""}
            </td>
            <td>
              <div className="ts-bold">APPROVED BY</div>
              <div>{approverName}</div>
              <Signature path={timeSheetDetails?.approved_by?.signature} />
            </td>
            <td>
              <div className="ts-bold">DATE APPROVED</div>
              {approvedAt.isValid() ? approvedAt.format("DD/MM/YYYY") : ""}
            </td>
          </tr>
        </tbody>
      </table>
      <div className="ts-muted" style={{ marginTop: 4, textAlign: "right" }}>
        Printed on {dayjs().format("DD/MM/YYYY HH:mm")}
      </div>
    </div>
  );
};

export default PrintTimeSheet;
